import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, Inbox, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { HERO_IMG } from "@/components/Hero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

const inputClass =
  "w-full rounded-none border border-black/20 bg-white px-4 py-3 text-sm text-royal-dark outline-none transition-[border-color,box-shadow] duration-200 focus:border-royal focus:ring-2 focus:ring-royal/30";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const loadEstimates = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/estimates`, { withCredentials: true });
      setEstimates(data);
    } catch {
      setEstimates([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadEstimates();
  }, [user, loadEstimates]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setEstimates([]);
  };

  const deleteEstimate = async (id) => {
    try {
      await axios.delete(`${API}/estimates/${id}`, { withCredentials: true });
      setEstimates((prev) => prev.filter((e) => e.id !== id));
      toast.success("Request deleted");
    } catch {
      toast.error("Could not delete — try again");
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-royal" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="relative flex min-h-screen items-center justify-center px-6" data-testid="admin-login-page">
        <div className="fixed inset-0 -z-10 bg-white" aria-hidden="true">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,#ffffff_0%,rgba(255,255,255,0.92)_12%,rgba(255,255,255,0.72)_30%,rgba(255,255,255,0.55)_55%,rgba(255,255,255,0.45)_100%)]" />
        </div>
        <header className="fixed inset-x-0 top-0 z-50 bg-white" data-testid="admin-login-header">
          <div className="relative flex h-16 w-full items-center px-6">
            <img
              src="/tcc-logo.png"
              alt="Trumbull Contracting Corporation logo"
              className="h-10 w-auto"
              data-testid="admin-login-logo"
            />
            <span className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 text-center leading-none">
              <span className="block font-display text-xl font-bold uppercase tracking-tight text-black">
                Trumbull
              </span>
              <span className="mt-0.5 flex items-center justify-center gap-1 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-royal-dark">
                <span className="h-px w-3 bg-royal-dark/80" />
                Paving Co.
                <span className="h-px w-3 bg-royal-dark/80" />
              </span>
            </span>
          </div>
        </header>
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-xs border border-black bg-white p-6 shadow-[8px_8px_0_0_#003399]"
        >
          <h1 className="mb-6 font-display text-2xl font-medium uppercase text-royal-dark" data-testid="admin-login-title">
            Admin Inbox
          </h1>
          <div className="space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className={inputClass}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputClass}
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={busy}
            className="mt-6 flex w-full items-center justify-center gap-2 bg-black px-6 py-3 font-mono text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-black/75 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-royal-dark/15 bg-white">
        <div className="relative flex h-16 w-full items-center px-6">
          <div className="flex items-center" data-testid="admin-wordmark">
            <img
              src="/tcc-logo.png"
              alt="Trumbull Contracting Corporation logo"
              className="h-10 w-auto"
            />
          </div>
          <span className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 text-center leading-none">
            <span className="block font-display text-xl font-bold uppercase tracking-tight text-black">
              Trumbull
            </span>
            <span className="mt-0.5 flex items-center justify-center gap-1 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-royal-dark">
              <span className="h-px w-3 bg-royal-dark/80" />
              Paving Co.
              <span className="h-px w-3 bg-royal-dark/80" />
            </span>
          </span>
        </div>
      </header>

      <div className="fixed inset-x-0 top-16 z-40 border-b border-royal-dark/10 bg-white" data-testid="admin-toolbar">
        <div className="flex w-full items-center justify-between gap-1.5 px-2 py-2">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="border border-royal-dark/40 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-royal-dark" data-testid="estimate-count">
              {estimates.length} request{estimates.length === 1 ? "" : "s"}
            </span>
            <button
              data-testid="admin-refresh-button"
              onClick={loadEstimates}
              className="flex items-center gap-1.5 border border-royal-dark/50 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-royal-dark transition-colors duration-100 active:bg-royal-dark active:text-white"
            >
              <RefreshCw className={`h-3 w-3 ${loadingList ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-1.5 bg-black px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-white transition-colors duration-100 active:bg-black/75"
          >
            <LogOut className="h-3 w-3" />
            Logout
          </button>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-6 pb-8 pt-[140px]">
        {estimates.length === 0 && !loadingList ? (
          <div className="flex flex-col items-center gap-4 border border-dashed border-black/20 bg-white py-24" data-testid="estimates-empty-state">
            <Inbox className="h-10 w-10 text-royal-dark/30" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-royal-dark/50">
              No estimate requests yet
            </p>
          </div>
        ) : (
          <>
          <div className="space-y-3 lg:hidden" data-testid="estimates-cards">
            {estimates.map((est) => (
              <div key={est.id} className="border border-black/10 bg-white p-4" data-testid={`estimate-card-${est.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-base font-semibold text-royal-dark">{est.name}</p>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={`px-2 py-0.5 font-mono text-[10px] ${est.email_sent ? "bg-royal/10 text-royal" : "bg-royal-dark/5 text-royal-dark/50"}`}>
                      {est.email_sent ? "Sent" : "Saved"}
                    </span>
                    <button
                      data-testid={`delete-estimate-${est.id}`}
                      onClick={() => deleteEstimate(est.id)}
                      aria-label="Delete request"
                      className="text-royal-dark/30 transition-colors duration-150 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-sm text-royal-dark/80">{est.address}</p>
                <a href={`tel:${est.phone}`} className="mt-1 block text-sm font-semibold text-royal">
                  {est.phone}
                </a>
                {est.notes ? <p className="mt-2 text-sm text-royal-dark/60">{est.notes}</p> : null}
                <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-royal-dark/50">
                  {new Date(est.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" })}
                </p>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto border border-black/10 bg-white lg:block" data-testid="estimates-table">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-black/10 bg-royal-dark/[0.03] font-mono text-xs uppercase tracking-[0.15em] text-royal-dark/60">
                  <th className="px-4 py-3 font-normal">Received</th>
                  <th className="px-4 py-3 font-normal">Name</th>
                  <th className="px-4 py-3 font-normal">Address</th>
                  <th className="px-4 py-3 font-normal">Phone</th>
                  <th className="px-4 py-3 font-normal">Notes</th>
                  <th className="px-4 py-3 font-normal">Emailed</th>
                  <th className="px-4 py-3 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {estimates.map((est) => (
                  <tr key={est.id} className="border-b border-black/5 transition-colors duration-150 hover:bg-royal/5" data-testid={`estimate-row-${est.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-royal-dark/50">
                      {new Date(est.created_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-3 font-semibold text-royal-dark">{est.name}</td>
                    <td className="px-4 py-3 text-royal-dark/80">{est.address}</td>
                    <td className="px-4 py-3">
                      <a href={`tel:${est.phone}`} className="text-royal hover:underline">{est.phone}</a>
                    </td>
                    <td className="max-w-[220px] px-4 py-3 text-royal-dark/60">{est.notes || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 font-mono text-xs ${est.email_sent ? "bg-royal/10 text-royal" : "bg-royal-dark/5 text-royal-dark/50"}`}>
                        {est.email_sent ? "Sent" : "Saved"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`delete-estimate-${est.id}`}
                        onClick={() => deleteEstimate(est.id)}
                        aria-label="Delete request"
                        className="text-royal-dark/30 transition-colors duration-150 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </main>
    </div>
  );
}
